/* eslint-disable */
import type { BackgroundCompositorConfig } from '../core/types'

/**
 * Native compositor that combines a foreground (person) with a
 * background effect (blur, image, or video) using an OffscreenCanvas.
 *
 * Uses the confidence mask from {@link MediaPipeSegmenter} to separate
 * person from background with optional edge refinement via a mask blur.
 */
export class BackgroundCompositor {
  private canvas: OffscreenCanvas | null = null
  private ctx: OffscreenCanvasRenderingContext2D | null = null

  private maskCanvas: OffscreenCanvas | null = null
  private maskCtx: OffscreenCanvasRenderingContext2D | null = null

  private bgCanvas: OffscreenCanvas | null = null
  private bgCtx: OffscreenCanvasRenderingContext2D | null = null

  private config: BackgroundCompositorConfig

  private bgImage: ImageBitmap | null = null
  private videoElement: HTMLVideoElement | null = null

  constructor (config: Partial<BackgroundCompositorConfig> = {}) {
    this.config = {
      maskBlurRadius: config.maskBlurRadius ?? 4,
      edgeSmoothing: config.edgeSmoothing ?? true,
      maskExpansion: config.maskExpansion ?? 2
    }
  }

  /**
   * Composite a frame with Gaussian background blur.
   */
  compositeBlur (
    frame: VideoFrame,
    mask: Float32Array,
    blurRadius: number
  ): VideoFrame {
    const width = frame.displayWidth
    const height = frame.displayHeight
    this.ensureCanvases(width, height)

    this.drawMask(mask, width, height)

    const ctx = this.ctx!

    // 1. Draw blurred frame as background
    ctx.clearRect(0, 0, width, height)
    ctx.filter = `blur(${blurRadius}px)`
    ctx.drawImage(frame as any, 0, 0, width, height)
    ctx.filter = 'none'

    // 2. Cut out person region from blurred background
    ctx.save()
    ctx.globalCompositeOperation = 'destination-out'
    ctx.drawImage(this.maskCanvas!, 0, 0)
    ctx.restore()

    // 3. Overlay original (unblurred) person on top
    ctx.save()
    ctx.globalCompositeOperation = 'source-over'
    this.drawMaskedForeground(frame, mask, width, height)
    ctx.restore()

    return new VideoFrame(this.canvas! as any, {
      timestamp: frame.timestamp || 0
    })
  }

  /**
   * Composite a frame with a static image background.
   */
  compositeImage (
    frame: VideoFrame,
    mask: Float32Array
  ): VideoFrame {
    const width = frame.displayWidth
    const height = frame.displayHeight
    this.ensureCanvases(width, height)

    this.drawMask(mask, width, height)

    const ctx = this.ctx!

    ctx.clearRect(0, 0, width, height)

    if (this.bgImage) {
      this.drawCover(ctx, this.bgImage, width, height)
    }

    ctx.save()
    ctx.globalCompositeOperation = 'destination-out'
    ctx.drawImage(this.maskCanvas!, 0, 0)
    ctx.restore()

    ctx.save()
    ctx.globalCompositeOperation = 'source-over'
    this.drawMaskedForeground(frame, mask, width, height)
    ctx.restore()

    return new VideoFrame(this.canvas! as any, {
      timestamp: frame.timestamp || 0
    })
  }

  /**
   * Composite a frame with a video background.
   */
  compositeVideo (
    frame: VideoFrame,
    mask: Float32Array
  ): VideoFrame {
    const width = frame.displayWidth
    const height = frame.displayHeight
    this.ensureCanvases(width, height)

    this.drawMask(mask, width, height)

    const ctx = this.ctx!

    ctx.clearRect(0, 0, width, height)

    if (this.videoElement && this.videoElement.readyState >= 2) {
      this.drawCover(ctx, this.videoElement, width, height)
    }

    ctx.save()
    ctx.globalCompositeOperation = 'destination-out'
    ctx.drawImage(this.maskCanvas!, 0, 0)
    ctx.restore()

    ctx.save()
    ctx.globalCompositeOperation = 'source-over'
    this.drawMaskedForeground(frame, mask, width, height)
    ctx.restore()

    return new VideoFrame(this.canvas! as any, {
      timestamp: frame.timestamp || 0
    })
  }

  /**
   * Load a background image from a URL.
   */
  async loadBackgroundImage (url: string): Promise<void> {
    if (this.bgImage) {
      this.bgImage.close()
      this.bgImage = null
    }

    const response = await fetch(url)
    const blob = await response.blob()
    this.bgImage = await createImageBitmap(blob)
  }

  /**
   * Load a background video element. The video must be playing or ready
   * to play for compositing to work.
   */
  loadBackgroundVideo (videoUrl: string): HTMLVideoElement {
    this.cleanupVideo()

    const video = document.createElement('video')
    video.crossOrigin = 'anonymous'
    video.loop = true
    video.muted = true
    video.playsInline = true
    video.src = videoUrl
    video.play().catch(() => {})
    this.videoElement = video
    return video
  }

  updateConfig (config: Partial<BackgroundCompositorConfig>): void {
    this.config = { ...this.config, ...config }
  }

  getConfig (): BackgroundCompositorConfig {
    return { ...this.config }
  }

  destroy (): void {
    this.canvas = null
    this.ctx = null
    this.maskCanvas = null
    this.maskCtx = null
    this.bgCanvas = null
    this.bgCtx = null

    if (this.bgImage) {
      this.bgImage.close()
      this.bgImage = null
    }

    this.cleanupVideo()
  }

  // ─── Private Helpers ─────────────────────────────────────────────

  private ensureCanvases (width: number, height: number): void {
    if (!this.canvas || this.canvas.width !== width || this.canvas.height !== height) {
      this.canvas = new OffscreenCanvas(width, height)
      this.ctx = this.canvas.getContext('2d') as OffscreenCanvasRenderingContext2D
    }

    if (!this.maskCanvas || this.maskCanvas.width !== width || this.maskCanvas.height !== height) {
      this.maskCanvas = new OffscreenCanvas(width, height)
      this.maskCtx = this.maskCanvas.getContext('2d') as OffscreenCanvasRenderingContext2D
    }

    if (!this.bgCanvas || this.bgCanvas.width !== width || this.bgCanvas.height !== height) {
      this.bgCanvas = new OffscreenCanvas(width, height)
      this.bgCtx = this.bgCanvas.getContext('2d') as OffscreenCanvasRenderingContext2D
    }
  }

  /**
   * Render the confidence mask to an offscreen canvas as a grayscale
   * alpha image with smooth edge transitions.
   *
   * Uses a Hermite smoothStep curve to map raw confidence values to
   * clean alpha transitions. The `maskExpansion` parameter controls
   * how generously edge pixels (like fingertips with low confidence)
   * are included — critical for sign language communication.
   *
   * Pipeline:  raw confidence → smoothStep curve → single Gaussian blur
   */
  private drawMask (mask: Float32Array, width: number, height: number): void {
    const maskCtx = this.maskCtx! as any
    const imageData = maskCtx.createImageData(width, height) as ImageData
    const pixels = imageData.data

    const lowerEdge = Math.max(0.02, 0.40 - this.config.maskExpansion * 0.12)
    const upperEdge = 0.75

    for (let i = 0; i < mask.length; i++) {
      const confidence = this.smoothStep(mask[i], lowerEdge, upperEdge)

      const offset = i * 4
      pixels[offset] = 255
      pixels[offset + 1] = 255
      pixels[offset + 2] = 255
      pixels[offset + 3] = Math.round(confidence * 255)
    }

    maskCtx.putImageData(imageData, 0, 0)

    if (this.config.edgeSmoothing && this.config.maskBlurRadius > 0) {
      maskCtx.filter = `blur(${this.config.maskBlurRadius}px)`
      maskCtx.drawImage(this.maskCanvas!, 0, 0)
      maskCtx.filter = 'none'
    }
  }

  /**
   * Hermite interpolation producing a smooth S-curve from 0 to 1.
   * No hard steps or visible borders — mathematically C1 continuous.
   *
   *   x < edge0  → 0  (background)
   *   x > edge1  → 1  (person)
   *   between    → smooth cubic transition
   */
  private smoothStep (x: number, edge0: number, edge1: number): number {
    const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)))
    return t * t * (3 - 2 * t)
  }

  /**
   * Draw the person foreground using the mask. Draws the original frame
   * clipped by the person mask.
   */
  private drawMaskedForeground (
    frame: VideoFrame,
    mask: Float32Array,
    width: number,
    height: number
  ): void {
    const bgCtx = this.bgCtx!
    bgCtx.clearRect(0, 0, width, height)
    bgCtx.drawImage(frame as any, 0, 0, width, height)

    bgCtx.save()
    bgCtx.globalCompositeOperation = 'destination-in'
    bgCtx.drawImage(this.maskCanvas!, 0, 0)
    bgCtx.restore()

    this.ctx!.drawImage(this.bgCanvas!, 0, 0)
  }

  /**
   * Draw a source onto the canvas using `object-fit: cover` semantics
   * so the entire canvas area is covered without distortion.
   */
  private drawCover (
    ctx: OffscreenCanvasRenderingContext2D,
    source: CanvasImageSource,
    canvasW: number,
    canvasH: number
  ): void {
    let srcW: number, srcH: number

    if (source instanceof ImageBitmap) {
      srcW = source.width
      srcH = source.height
    } else if (source instanceof HTMLVideoElement) {
      srcW = source.videoWidth || canvasW
      srcH = source.videoHeight || canvasH
    } else {
      srcW = canvasW
      srcH = canvasH
    }

    const scale = Math.max(canvasW / srcW, canvasH / srcH)
    const drawW = srcW * scale
    const drawH = srcH * scale
    const dx = (canvasW - drawW) / 2
    const dy = (canvasH - drawH) / 2

    ctx.drawImage(source, dx, dy, drawW, drawH)
  }

  private cleanupVideo (): void {
    if (this.videoElement) {
      this.videoElement.pause()
      this.videoElement.removeAttribute('src')
      this.videoElement.load()
      this.videoElement = null
    }
  }
}
